import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SearchIcon, BellIcon, UserIcon } from "../../../../shared/cockpit/components/icons/Icon";
import { useCurrentUser, useLogout } from "../../auth/services/AuthService";
import "./Header.scss";

/**
 * Header der AppShell.
*/
export default function Header() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Aktuell eingeloggten User laden, um den displayName anzuzeigen.
  const { data: currentUser } = useCurrentUser(true);
  const displayName = currentUser?.displayName ?? "";

  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const logoutMutation = useLogout();

  // Dropdown schliesst sich, wenn ausserhalb geklickt wird.
  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isUserMenuOpen]);

  const handleToggleUserMenu = () => {
    setIsUserMenuOpen((prev) => !prev);
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        // Nach erfolgreichem Logout zur Landingpage navigieren.
        navigate("/", { replace: true });
      },
      onError: (err) => {
        // Auch im Fehlerfall zur Landingpage navigieren, da der Cookie
        // serverseitig ohnehin geloescht wurde.
        console.error("Logout fehlgeschlagen", err);
        navigate("/", { replace: true });
      },
    });
  };

  return (
    <header className="app-shell-header">
      {/* Links: Logo */}
      <div className="app-shell-header__brand">
        <div className="app-shell-header__logo">A&A</div>
        <span className="app-shell-header__company">Globalreach GmbH</span>
      </div>

      {/* Mitte: Globale Suche */}
      <div className="app-shell-header__search">
        <SearchIcon className="app-shell-header__search-icon" />
        <input
          type="text"
          className="app-shell-header__search-input"
          placeholder="Suche (Kunden, Projekte, IDs...)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Rechts: Benachrichtigungen + User-Menü */}
      <div className="app-shell-header__actions">
        <button
          className="app-shell-header__notification"
          aria-label="Benachrichtigungen"
        >
          <BellIcon className="app-shell-header__notification-icon" />
          <span className="app-shell-header__notification-badge">3</span>
        </button>

        <div
          className="app-shell-header__user"
          ref={userMenuRef}
        >
          <button
            type="button"
            className="app-shell-header__user-trigger"
            onClick={handleToggleUserMenu}
            aria-haspopup="menu"
            aria-expanded={isUserMenuOpen}
            aria-label="Benutzermenü öffnen"
          >
            <div className="app-shell-header__avatar">
              <UserIcon className="app-shell-header__avatar-icon" />
            </div>
            <div className="app-shell-header__user-info">
              <span className="app-shell-header__user-name">{displayName}</span>
            </div>
            <span
              className={`app-shell-header__user-caret ${
                isUserMenuOpen ? "app-shell-header__user-caret--open" : ""
              }`}
              aria-hidden="true"
            >
              ▾
            </span>
          </button>

          {isUserMenuOpen && (
            <div
              className="app-shell-header__user-menu"
              role="menu"
            >
              <div className="app-shell-header__user-menu-header">
                <span className="app-shell-header__user-menu-name">
                  {displayName}
                </span>
                {currentUser?.email && (
                  <span className="app-shell-header__user-menu-username">
                    {currentUser.email}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="app-shell-header__user-menu-item"
                role="menuitem"
                onClick={handleLogout}
                disabled={logoutMutation.isPending}
              >
                {logoutMutation.isPending
                  ? "Wird abgemeldet..."
                  : "Abmelden"}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
